<script setup>
import { i18n } from 'boot/i18n';
// App chrome, not app logic: every page renders the same route-derived breadcrumb, and one that
// dropped it would look like a different product. Shell only — nothing about billing comes from here.
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs.vue';
// The app's filtered table rather than the design system's, so this reads like Clients: saved
// column choices, saved filters, and the export affordance every other list already has. It is the
// second app component this context reaches for, after the breadcrumb, and for the same reason —
// it is chrome the product is consistent about, not anything about billing.
import RecordsTable from 'components/UI/RecordsTable.vue';
import { filterPropertyTypes } from 'src/constants/filters';
import {
  HrEmptyState,
  HrInfoBanner,
  HrOverflowMenuBtn,
  HrPage,
  HrSubtleBtn,
  HrSupportingBtn,
  notifyNegative,
  notifySuccess,
} from 'src/design-system';
import { computed, onMounted, ref, watch } from 'vue';

import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';

import { exportTableDataToSpreadsheet } from 'src/utils/spreadsheet';

import { isBlockedBy } from '../../domain/constants/holdTaskCatalog';
import { HOLD_SUBJECT, HOLD_SUBJECT_FIELD } from '../../domain/constants/holdTaskTypes';
import { fixFor } from '../../domain/services/holdReasonFix';
import { useBillingExport } from '../composables/useBillingExport';
import { useBillingItemLookups } from '../composables/useBillingItemLookups';
import { useBillingWorklist } from '../composables/useBillingWorklist';
import { useFundingSourceVersions } from '../composables/useFundingSourceVersions';
import { useHoldSubjectNames } from '../composables/useHoldSubjectNames';
import { billingExportName, exportColumns } from '../presenters/billingExportPresenter';
import { billingItemRowActions } from '../presenters/billingItemRowActions';
import { presentBillingItemRow } from '../presenters/billingItemRowPresenter';

/**
 * What a clinic is about to bill for a period, and what it cannot bill yet.
 *
 * TWO HALVES OF ONE LIST, NOT TWO LISTS. A biller's question is "what happened in September", and a
 * screen that separated the billable from the blocked would answer it twice and let the second half
 * go unread — which is the half that needs somebody to act. The status column carries the difference,
 * and the reason sits on the row that has one.
 *
 * NOTHING HERE CHANGES AN ITEM, and the absence is the design rather than a gap. An item is DERIVED
 * from a session and the configuration around it, so an edit made here would be recomputed away by
 * the next projection run: the fix for a wrong row is to fix what it came from. So a held row's Fix
 * opens the editor for that record, and the live list shows the row recover once it is re-projected.
 *
 * IT READS A PERIOD, NOT A PAGE. The window is the query's only constraint; payer and state narrow
 * what was read, in memory. See the composable for why, and for what would have to change first if a
 * clinic's month ever stops fitting.
 */
defineOptions({ name: 'BillingItemsPage' });

/**
 * THE EDITORS ARE THE HOST'S, supplied as `fixSurfaces`: they import this context back, so opening
 * them from here would be an import cycle. Without a host, rows offer nothing to open.
 */
const props = defineProps({
  /** @type {import('vue').PropType<import('../../application/ports/FixSurfaces.port').FixSurfaces|null>} */
  fixSurfaces: {
    type: Object,
    default: null,
  },
  /**
   * The items about one client, payer, treatment service or code, when a link narrowed the list to
   * them; the host holds it in the URL. Narrowed before the table's own filters, which are the
   * biller's and saved per browser.
   * @type {import('vue').PropType<{kind: 'client'|'payer'|'treatmentService'|'code'|'sessionActivity'|'staff',
   *   id: string}|null>}
   */
  narrowTo: {
    type: Object,
    default: null,
  },
  /** How the host reads a session plan's name, which billing does not own; without it plans go unnamed. */
  readSessionPlanName: {
    type: Function,
    default: null,
  },
  /**
   * The task type whose blocked items a worklist link asked for. Without it a task's link would
   * list everything for its subject, ready items included, under a button counting only the blocked.
   */
  blockedBy: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** The biller asked to see every client's items again. */
  'show-all': null,
});

const authStore = useAuthStore();
const clientsStore = useClientsStore();
const worklist = useBillingWorklist();
const funders = useFundingSourceVersions();
const billingExport = useBillingExport();

/**
 * The clinic's payers, held here rather than in the composable.
 *
 * The read RETURNS rather than holds, because the composable's own state is about one payer's
 * timeline while this is a clinic-wide lookup. Every payer configured, including one that has not
 * started yet: an item held because its payer is not in effect still has to say which payer.
 */
const payers = ref([]);

/** The tenant. Every read on this page is scoped by it. */
const clinicId = computed(() => authStore.userDetails?.clinic ?? null);

/**
 * NEWEST WORK FIRST, which the query's order does not give: it returns the clinic's whole history
 * ascending, so a table with no default sort opens on the oldest rows the clinic ever projected and
 * every biller's first action is to sort. On the raw calendar date; see the column for why.
 */
const pagination = ref({ rowsPerPage: 25, sortBy: 'serviceDate', descending: true });

/** The rows a biller has checked. The table clears it when the filters change. */
const selected = ref([]);

/**
 * A FAILED READ IS SAID OUT LOUD, because nothing else here would say it. The table would otherwise
 * render its "nothing to bill" label, which is the one message that is certainly wrong — and which
 * reads as "this clinic delivered nothing" rather than "we could not look". Offline and a session
 * with no clinic arrive this way. A denied read does not: the table says it in place, since
 * retrying cannot help.
 */
const reportLoadFailure = (error) => {
  notifyNegative(i18n.t('billingItems.notifyLoadFailed'));
  console.error('Failed to load billing items', error);
};

onMounted(async () => {
  if (!clinicId.value) {
    return;
  }
  worklist.subscribe({ clinicId: clinicId.value, onError: reportLoadFailure });

  try {
    payers.value = (await funders.loadConfigured({ clinicId: clinicId.value })) ?? [];
  } catch (error) {
    notifyNegative(i18n.t('billingItems.notifyPayersLoadFailed'));
    console.error('Failed to load payers', error);
  }
});

/** Resolved from what the app already holds: an item stores references and no names. */
const lookups = useBillingItemLookups({
  clients: computed(() => clientsStore.clients),
  payers,
  staff: computed(() => clientsStore.staffMembers),
});

const subjectNames = useHoldSubjectNames({ payers, readSessionPlanName: props.readSessionPlanName });
const narrowingField = computed(() => HOLD_SUBJECT_FIELD[props.narrowTo?.kind] ?? null);

const rows = computed(() => worklist.items.value
  .filter((item) => !narrowingField.value || item[narrowingField.value] === props.narrowTo.id)
  .filter((item) => !props.blockedBy || isBlockedBy(item, props.blockedBy))
  .map((item) => ({
    ...presentBillingItemRow(item, lookups.value),
    fix: fixFor(item),
  })));

/** A session is named by its client too, which its own items say. */
const narrowedClientId = computed(() => (props.narrowTo?.kind === HOLD_SUBJECT.SESSION
  ? worklist.items.value.find((item) => item.sessionActivityId === props.narrowTo.id)?.clientId ?? null
  : null));

const narrowedTo = computed(() => (narrowingField.value
  ? subjectNames.nameOf(props.narrowTo.kind, props.narrowTo.id, narrowedClientId.value) ?? props.narrowTo.id
  : null));

/**
 * A LINK'S LIST STARTS FROM NO SAVED VIEW. The table restores the biller's own filters and view, and
 * a status filter saved there would hide the very items a task sent them to, under a banner saying
 * they are shown. Keyed on it too, so leaving the link puts the biller's own view back.
 */
const followsLink = computed(() => Boolean(props.narrowTo || props.blockedBy));

/** Says what a link narrowed the list to, so it is not mistaken for everything the clinic has to bill. */
const narrowedMessage = computed(() => {
  const task = props.blockedBy ? i18n.t(`billingItems.holdTask.${props.blockedBy}.title`) : null;

  if (task && narrowedTo.value) {
    return i18n.t('billingItems.textShowingBlocked', { subject: narrowedTo.value, task });
  }
  if (task) {
    return i18n.t('billingItems.textShowingBlockedAll', { task });
  }

  return narrowedTo.value ? i18n.t('billingItems.textShowingSubject', { subject: narrowedTo.value }) : null;
});

/**
 * THE CHECKED ROWS FOLLOW THE LIVE READ. The table matches its boxes by id, so a box stays ticked
 * across a snapshot while `selected` keeps the object it was ticked on — and that object is what the
 * export writes and stamps. A row that went on hold since would be stamped as billed.
 */
watch(rows, (current) => {
  const byId = new Map(current.map((row) => [row.id, row]));
  selected.value = selected.value.map((row) => byId.get(row.id)).filter(Boolean);
});

const openActivity = (row) => {
  if (props.fixSurfaces && row.sessionActivityId) {
    props.fixSurfaces.openActivity(row.sessionActivityId);
  }
};

const rowActions = (row) => billingItemRowActions(row, props.fixSurfaces);

/** Only the payers present in what was loaded, so the filter is not a list of empty results. */
const payerFilterItems = computed(() => [...new Set(
  rows.value.map((row) => row.payerName).filter(Boolean),
)].map((name) => ({ value: name, label: name })));

/**
 * What a biller sees before touching anything. The rest stay available in the column picker: a
 * claim needs all of them, but a screen that opens with ten columns is read by nobody.
 */
const defaultColumns = [
  'serviceDate', 'clientName', 'payerName', 'code', 'charge', 'status', 'exportStateLabel',
];

/**
 * SERVICE DATE SORTS ON THE RAW VALUE, not the formatted one: a formatted date sorts alphabetically,
 * which puts April before January. The presenter carries both for exactly this reason.
 */
const columns = computed(() => [
  {
    name: 'serviceDate',
    label: i18n.t('billingItems.columnServiceDate'),
    field: 'serviceDate',
    type: 'date',
    format: (value, row) => row.serviceDateLabel,
    // THE FILE GETS THE CALENDAR DATE, not the formatted one. A short date is ambiguous between
    // reading conventions, and this file is read by whoever keys the claim — possibly a billing
    // service in another country.
    exportFormatExcel: (value) => value,
    align: 'left',
    sortable: true,

    // THE PERIOD IS A FILTER LIKE ANY OTHER, not a pair of pickers above the table. A biller asking
    // for September asks the same way they ask for a payer, and the answer comes from what is
    // already loaded rather than from another read.
    filterable: true,
    filterType: filterPropertyTypes.Date,
    // NOT `serviceDate`: the date filter refuses anything that is not a Firestore Timestamp, so a
    // calendar date matches nothing at all rather than comparing wrongly. The row carries both.
    filterProperty: 'serviceDateAt',
  },
  {
    name: 'clientName',
    label: i18n.t('billingItems.columnClient'),
    field: 'clientName',
    align: 'left',
    sortable: true,
  },
  {
    name: 'payerName',
    label: i18n.t('billingItems.columnPayer'),
    field: 'payerName',
    align: 'left',
    sortable: true,

    // OFFERED FROM WHAT IS LOADED, not from every payer the clinic ever configured: the one a
    // biller wants is by definition on a row in front of them, and the rest are empty results.
    filterable: true,
    filterType: filterPropertyTypes.StringInArray,
    filterProperty: 'payerName',
    filterItems: payerFilterItems.value,
  },
  {
    name: 'code',
    label: i18n.t('billingItems.columnCode'),
    field: 'code',
    align: 'left',
  },
  {
    name: 'diagnosisCodes',
    label: i18n.t('billingItems.columnDiagnosis'),
    field: 'diagnosisCodes',
    align: 'left',
  },
  {
    name: 'charge',
    label: i18n.t('billingItems.columnCharge'),
    field: 'charge',
    align: 'right',
    // A NUMBER IN THE FILE, the written amount on screen. The biller multiplies by the units they
    // worked out and sums the column, neither of which works on "€10.00".
    exportFormatExcel: (value, row) => row.chargeAmount,
  },
  {
    name: 'chargeCurrency',
    label: i18n.t('billingItems.columnCurrency'),
    field: 'chargeCurrency',
    align: 'left',
  },
  {
    name: 'placeOfServiceCode',
    label: i18n.t('billingItems.columnPlaceOfService'),
    field: 'placeOfServiceCode',
    align: 'left',
  },
  {
    name: 'renderingProvider',
    label: i18n.t('billingItems.columnRenderingProvider'),
    field: 'renderingProvider',
    align: 'left',
  },
  /*
   * WHAT A BILLER HANDS ON, rather than what they read. None of these are shown by default: the
   * screen answers "what will bill and what will not", and a claim needs more than that. They are
   * one click away in the column picker, and the file carries them whether they are showing or not.
   */
  {
    name: 'serviceStartLabel',
    label: i18n.t('billingItems.columnServiceStart'),
    field: 'serviceStartLabel',
    align: 'left',
  },
  {
    name: 'serviceEndLabel',
    label: i18n.t('billingItems.columnServiceEnd'),
    field: 'serviceEndLabel',
    align: 'left',
  },
  {
    name: 'memberId',
    label: i18n.t('billingItems.columnMemberId'),
    field: 'memberId',
    align: 'left',
  },
  {
    name: 'authorizationNumbers',
    label: i18n.t('billingItems.columnAuthorizationNumber'),
    field: 'authorizationNumbers',
    align: 'left',
  },
  {
    name: 'policyholderRelationship',
    label: i18n.t('billingItems.columnPolicyholderRelationship'),
    field: 'policyholderRelationship',
    align: 'left',
  },
  {
    name: 'durationMinutes',
    label: i18n.t('billingItems.columnDuration'),
    field: 'durationMinutes',
    align: 'right',
    sortable: true,
  },
  {
    name: 'modifiers',
    label: i18n.t('billingItems.columnModifiers'),
    field: 'modifiers',
    align: 'left',
  },
  {
    name: 'renderingProviderNpi',
    label: i18n.t('billingItems.columnRenderingProviderNpi'),
    field: 'renderingProviderNpi',
    align: 'left',
  },
  {
    name: 'clientDateOfBirth',
    label: i18n.t('billingItems.columnClientDateOfBirth'),
    field: 'clientDateOfBirth',
    format: (value, row) => row.clientDateOfBirthLabel,
    // THE FILE GETS THE CALENDAR DATE, for the reason the service date does and more sharply: a
    // short date is ambiguous between reading conventions, and this is the field a payer matches a
    // patient on.
    exportFormatExcel: (value) => value,
    align: 'left',
  },
  {
    name: 'status',
    label: i18n.t('billingItems.columnStatus'),
    field: 'status',
    // A BLOCKED ROW SAYS WHY, in place of the status it does not have. Formatted rather than
    // slotted: a `body-cell` slot replaces the whole cell, so it has to render its own `q-td`, and
    // a slot that forgets one puts its content outside the table's cell flow. Nothing here needs
    // markup, so nothing here needs a slot.
    format: (value, row) => (row.isHeld ? row.holdReasonLabel : i18n.t('billingItems.statusReady')),
    align: 'left',

    // Narrowed on what the projection recorded, shown as the reason: "held" tells a biller nothing.
    filterable: true,
    filterType: filterPropertyTypes.StringInArray,
    filterProperty: 'status',
    filterItems: [
      { value: 'ready', label: i18n.t('billingItems.statusReady') },
      { value: 'held', label: i18n.t('billingItems.statusHeld') },
    ],
  },
  {
    name: 'exportStateLabel',
    label: i18n.t('billingItems.columnExportState'),
    field: 'exportStateLabel',
    align: 'left',

    // Filtered on the stored state rather than its label, so "what still needs exporting" survives
    // a change of wording.
    filterable: true,
    filterType: filterPropertyTypes.StringInArray,
    filterProperty: 'exportState',
    filterItems: ['never', 'exported', 'changedSinceExport'].map((value) => ({
      value,
      label: i18n.t(`billingItems.exportState.${value}`),
    })),
  },
]);

/**
 * The one thing a biller comes here to do. Nothing is created in a worklist — it is a projection of
 * what has already happened — so this is the page's primary action rather than a reaction to one.
 *
 * IT IS NOT THE TABLE'S EXPORT, which writes a file and changes nothing. This writes the same file
 * and then marks what went out as exported, which is a change to the records. The table hands over
 * what the biller is looking at, their rows and their columns, because only it knows that; the file
 * adds the hidden columns after them, and the stamping is the part only this context can do, as the
 * rules give billingItems no client write path at all.
 *
 * ONLY THE BILLABLE ROWS ARE STAMPED. A biller may export a view that includes blocked rows, and
 * seeing why something did not bill is a reasonable thing to take away — but a blocked row was not
 * billed, so marking it exported would hide it from the next export that should offer it.
 *
 * THE STAMP FAILING IS NOT THE EXPORT FAILING. The file is already in the biller's hands, so this
 * says what happened rather than pretending otherwise: the rows stay unexported and are offered
 * again, which is the safe direction. The stamped rows reach the list through the live read.
 */
const exportBillingItems = async (exportedRows, exportedColumns) => {
  await exportTableDataToSpreadsheet(
    exportedRows,
    exportColumns(exportedColumns, columns.value),
    `${billingExportName()}.xlsx`,
  );

  let outcome;

  try {
    outcome = await billingExport.recordExport({
      clinicId: clinicId.value,
      rows: exportedRows,
    });
  } catch (error) {
    notifyNegative(i18n.t('billingItems.notifyStampFailed'));
    console.error('Failed to record the billing export', error);
    return;
  }

  /*
   * The file went out, so the rows in it have done their job. Only those: the boxes stay live while
   * this runs, and a row checked meanwhile is a choice for the next export. Not before this point,
   * either: a stamp that failed leaves them checked, so a retry takes the same ones.
   */
  const exportedRowIds = new Set(exportedRows.map((row) => row.id));
  selected.value = selected.value.filter((row) => !exportedRowIds.has(row.id));

  // Nothing billable was in the file. Not an export, and nothing to say about it.
  if (outcome === null) {
    return;
  }

  notifySuccess(i18n.t('billingItems.notifyExported', { count: outcome.billableRows }));
};

const primaryAction = computed(() => ({
  key: 'export-billing-items',
  // Every row counted is a row this marks as exported, so the reader is told before pressing.
  label: (count) => i18n.tc('billingItems.buttonExport', count ?? 0, { count }),
  icon: 'sym_o_file_export',
  dataE2e: 'button-export-billing-items',
  // Takes the checked rows when there are any.
  takesRecords: true,
  handler: ({ rows: exportedRows, columns: exportedColumns }) => exportBillingItems(exportedRows, exportedColumns),
}));
</script>

<template>
  <hr-page>
    <breadcrumbs
      hide-client
      :no-link="['billing']"
    />
    <hr-info-banner
      v-if="narrowedMessage"
      class="q-mb-md"
      :message="narrowedMessage"
    >
      <template #action>
        <hr-supporting-btn
          :label="i18n.t('billingItems.buttonShowAll')"
          data-e2e="button-billing-items-show-all"
          @click="emit('show-all')"
        />
      </template>
    </hr-info-banner>
    <records-table
      :key="followsLink ? 'link' : 'saved'"
      v-model:pagination="pagination"
      v-model:selected="selected"
      :title="i18n.t('billingItems.titleBillingItems')"
      :default-view-label="i18n.t('billingItems.defaultViewLabel')"
      :search-placeholder="i18n.t('billingItems.inputSearch')"
      :rows="rows"
      :columns="columns"
      :default-columns="defaultColumns"
      :loading="worklist.loading.value"
      :no-data-label="worklist.denied.value
        ? i18n.t('billingItems.tableNoAccessLabel')
        : i18n.t('billingItems.tableNoDataLabel')"
      :save-filter-id="followsLink ? null : 'billingItems'"
      selection="multiple"
      :create="primaryAction"
      standalone
      disable-export
      @row-click="(event, row) => openActivity(row)"
    >
      <template #row-actions="{ row }">
        <hr-overflow-menu-btn
          v-if="rowActions(row).length > 0"
          :options="rowActions(row)"
          data-e2e="button-billing-item-actions"
          @click.stop
        />
      </template>
      <template #empty-filtered="{ hasSearchTerm, hasFilters, clearSearch, clearFilters }">
        <hr-empty-state
          icon="search_off"
          :title="i18n.t('billingItems.emptyFilteredTitle')"
          :message="i18n.t('billingItems.emptyFilteredMessage')"
        >
          <template #actions>
            <hr-supporting-btn
              v-if="hasSearchTerm"
              :label="i18n.t('common.buttonClearSearch')"
              data-e2e="button-clear-search"
              @click="clearSearch"
            />
            <hr-subtle-btn
              v-if="hasFilters"
              :label="i18n.t('common.buttonClearFilters')"
              data-e2e="button-clear-filters"
              @click="clearFilters"
            />
          </template>
        </hr-empty-state>
      </template>
    </records-table>
  </hr-page>
</template>
